import App from '../../../../../../app';
import type { GetLimitsHistoryPayload, GetPaymentsPayload } from '../../../../../mod-account/types';
import type { BECampaign, Bonus, SubscribeResponseDetails, SubscriptionAPI } from '../../../../../mod-bonus/types';
import { createQuery } from '../../../util/url';
import type { Channel } from '../casino/casino-domain';
import { type ApiDef, type Balance, fetchSettings } from '../types';
import {
  type CustomerPaymentDetails,
  type Deactivation,
  type DepositCashierSession,
  type LimitsHistory,
  type LoginOAuthBody,
  type PaymentHistoryItem,
  PrefKey,
  type UserActivitySummaryAPI,
  type UserConfirmType,
  type UserPreferences,
  type WalletBlockedAmount,
  type WalletTransaction,
  type WithdrawalCashierSession,
} from './pam-domain';

// -------------------------------

export function getBalance(jwt: string): ApiDef<Balance> {
  return {
    path: '/v1/api/wallet/balance',
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => body.balance),
  };
}

export function getBlockedAmount(jwt: string): ApiDef<WalletBlockedAmount> {
  return {
    path: '/v1/api/wallet/blocked-amount',
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json(),
  };
}

export async function loginOAuth(url: string, body: LoginOAuthBody): Promise<Response> {
  const casinoUrl = `${url}/v1/api/user/login/oauth`;
  return await fetch(casinoUrl, {
    ...fetchSettings,
    method: 'POST',
    headers: new Headers({
      'X-Client-Channel': App.instance.casino.getChannel(),
      'content-type': 'application/json',
    }),
    body: JSON.stringify(body),
  });
}

export async function loginGet(url: string, jwt: string): Promise<Response> {
  const pamUrl = `${url}/v1/api/user/login`;
  return await fetch(pamUrl, {
    ...fetchSettings,
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${jwt}`,
    },
  });
}

export function loginUpdate(jwt: string): ApiDef<any> {
  return {
    path: '/v1/api/user/login',
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        authorization: `Bearer ${jwt}`,
      },
    },
    mapper: (response) => response.json().then((body) => ({ ...body })),
  };
}

export const sessionHandlingUrl = '/v1/api/user/session';

export interface SessionHandlingResponse {
  token: string;
  deactivation?: Deactivation;
}

export async function sessionHandling(url: string, jwt: string, active: boolean): Promise<Response> {
  const pamUrl = `${url}${sessionHandlingUrl}?customerActivity=${active}`;
  return await fetch(pamUrl, {
    ...fetchSettings,
    method: 'GET',
    headers: {
      'X-Client-Channel': App.instance.casino.getChannel(),
      'content-type': 'application/json',
      authorization: `Bearer ${jwt}`,
    },
  });
}

export function logoutPam(jwt: string, queryObject?: any): ApiDef<void> {
  return {
    path: `/v1/api/user/logout${createQuery(queryObject)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'X-Client-Channel': App.instance.casino.getChannel(),
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
  };
}

export function acknowledgeLifetimeDeposit(jwt: string): ApiDef<any> {
  return {
    path: '/v1/api/payment/threshold/deposit',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      },
    },
  };
}

export function getClientIp(jwt: string): ApiDef<string> {
  return {
    path: '/v1/api/geolocation/ip',
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        authorization: `Bearer ${jwt}`,
      },
    },
    mapper: async (r) => (await r.json()).clientIp,
  };
}

export function acceptTerms(jwt: string, version: number): ApiDef<any> {
  return {
    path: '/v1/api/user/terms',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ version }),
    },
  };
}

export function bonusSubscriptions(
  jwt: string,
  options: {
    filter?: 'ALL' | 'ACTIVE' | 'INACTIVE';
    includeExpired?: boolean;
    includeConfig?: boolean;
    campaignIds?: string[] | null;
  },
): ApiDef<SubscriptionAPI[]> {
  (options.campaignIds as any) = options?.campaignIds?.join(',');
  if (!options.campaignIds) {
    options.campaignIds = null;
  }
  return {
    path: `/v1/api/bonus/subscriptions${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        authorization: `Bearer ${jwt}`,
      },
    },
  };
}

export function bonusBonuses(
  jwt: string,
  options: {
    state?: 'ALL' | 'ACTIVE';
    cutOff: '3M' | '1Y';
    tags?: [string];
  },
): ApiDef<Bonus[]> {
  return {
    path: `/v1/api/bonus/bonuses${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        authorization: `Bearer ${jwt}`,
      },
    },
  };
}

export function pastBonuses(
  jwt: string,
  options: {
    limit?: number;
    skip?: number;
    tags?: [string];
  },
): ApiDef<Bonus[]> {
  return {
    path: `/v1/api/bonus/bonuses/history${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        authorization: `Bearer ${jwt}`,
      },
    },
  };
}

export function bonusCampaigns(
  jwt: string,
  options: {
    ids?: string[];
    tags?: 'casino';
    validity?: 'ALL';
    filter?: 'ALL';
  },
): ApiDef<BECampaign[]> {
  (options.ids as any) = options?.ids?.join(',');
  return {
    path: `/v1/api/bonus/campaigns${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        authorization: `Bearer ${jwt}`,
      },
    },
  };
}

export function availableCampaigns(
  jwt: string | undefined,
  options: {
    tags: string;
    validity: 'VALID';
    ids?: string;
  },
): ApiDef<BECampaign[]> {
  return {
    path: `/v1/api/bonus/campaigns/all${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        authorization: jwt ? `Bearer ${jwt}` : '',
      },
    },
  };
}

export function availableWelcomeCampaigns(options: { tags: string; validity: 'VALID' }): ApiDef<BECampaign[]> {
  return {
    path: `/v1/api/bonus/campaigns/welcome${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
    },
  };
}

export function subscribeToCampaign(jwt: string, campaignId: string): ApiDef<SubscribeResponseDetails> {
  return {
    path: '/v1/api/bonus/subscriptions',
    requestDef: {
      ...fetchSettings,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ campaignId, resolveConstraints: true }),
    },
    mapper: (response) => response.json(),
  };
}

export function saveDepositTarget(jwt: string, subscriptionId: string): ApiDef<SubscribeResponseDetails> {
  return {
    path: `/v1/api/bonus/subscriptions/${subscriptionId}`,
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ activationCandidate: true }),
    },
    mapper: (response) => response.json(),
  };
}

export function claimWelcomeCampaign(jwt: string, campaignId: string): ApiDef<SubscribeResponseDetails> {
  return {
    path: '/v1/api/bonus/campaigns/welcome',
    requestDef: {
      ...fetchSettings,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ campaignId, resolveConstraints: true }),
    },
    mapper: (response) => response.json(),
  };
}

export function cancelSubscription(jwt: string, subscriptionId: string): ApiDef<void> {
  return {
    path: `/v1/api/bonus/campaigns/subscriptions/${subscriptionId}`,
    requestDef: {
      ...fetchSettings,
      method: 'DELETE',
      headers: {
        authorization: `Bearer ${jwt}`,
      },
    },
    mapper: () => Promise.resolve(void 0),
  };
}

export function cancelBonus(jwt: string, bonusId: string): ApiDef<void> {
  return {
    path: `/v1/api/bonus/bonuses/${bonusId}/cancel`,
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: () => Promise.resolve(void 0),
  };
}

// -------------------------------
// Cashier session creation (payment-fs)
//
// Mirrors the ReThink `payments-orchestrator` fetchers
// (`packages/mods/payments-orchestrator/mod/src/services/{deposit,withdrawal}/fetchers.ts`
// in `rethink-monoverse @ 8f9dd0a138a`): same path, same method, same three body
// fields. See `docs/integrations/cornucopia.md` → "Create the cashier session".
//
// These replace the PAM `/v1/api/payment/*-cashier-sessions` endpoints, which
// were retired and will not be reinstated. Note the prefix is `/v1/payfs/payint/`
// rather than PAM's `/v1/api/` — they are payment-fs endpoints, kept in this
// module only because the mount site already resolves them through the same
// `http.call(baseUrl, apiDef)` path.
//
// **Served from the account host, and authenticated by cookie.** The base URL is
// `appConfig.accountUrl` (see the call site in `cashier-view.ts`), and the request
// is credentialed because the endpoint authenticates on the `SESSION_ID` cookie
// — domain `.account.games.tipico.de`, i.e. scoped to the account host, never
// sent to our own origin. That cookie is the only credential: no `authorization`
// header is sent, because PayFS auth is either the `SESSION_ID` cookie or a
// Bearer jwt, never both (confirmed by PayTech, 2026-10-05).
// -------------------------------

const DEPOSIT_CASHIER_SESSION_PATH = '/v1/payfs/payint/payment/deposit-cashier-sessions';
const WITHDRAWAL_CASHIER_SESSION_PATH = '/v1/payfs/payint/payment/withdrawal-cashier-sessions';

/**
 * `channel` values the cashier-session endpoints expect.
 *
 * These are the Games and Casino members of ReThink's `ClientType` enum —
 * confirmed by reading it directly:
 * `packages/libs/utils/lib/src/models/ClientType.ts` in
 * `rethink-monoverse-master@04f3e2332c6` (`local-investigation/`). All 12
 * members (bare `*_RETH`, `*_CASINO_RETHINK`, `*_GAMES_RETHINK`) are declared
 * there verbatim. **Not** this repo's own `ClientType`, an unrelated
 * device-type union declared in `global.d.ts` — same name, different type,
 * different codebase. The Games four were additionally confirmed by the
 * payments team 2026-09-08.
 *
 * This is its own vocabulary, **not** the `Channel` values the rest of this
 * module sends as `X-Client-Channel`. `_NAT_` denotes the native app wrappers;
 * everything else is a browser.
 *
 * ReThink's `ClientType` carries three parallel brand sets — bare `*_RETH`
 * (Sports), `*_CASINO_RETHINK`, and `*_GAMES_RETHINK`. Both brand sets used by
 * this repo are exported here so a caller can select per-brand rather than
 * hardcoding one.
 */
export const CashierChannel = {
  Games: {
    IosNative: 'IOS_NAT_GAMES_RETHINK',
    AndroidNative: 'ANDROID_NAT_GAMES_RETHINK',
    MobileWeb: 'MOBILE_WEB_GAMES_RETHINK',
    DesktopWeb: 'DESKTOP_WEB_GAMES_RETHINK',
  },
  Casino: {
    IosNative: 'IOS_NAT_CASINO_RETHINK',
    AndroidNative: 'ANDROID_NAT_CASINO_RETHINK',
    MobileWeb: 'MOBILE_WEB_CASINO_RETHINK',
    DesktopWeb: 'DESKTOP_WEB_CASINO_RETHINK',
  },
} as const;

export type CashierChannel =
  (typeof CashierChannel)[keyof typeof CashierChannel][keyof (typeof CashierChannel)['Games']];

/** One brand's four `channel` values — `CashierChannel.Games`, `CashierChannel.Casino`, … */
export type CashierChannelSet = Readonly<Record<keyof (typeof CashierChannel)['Games'], CashierChannel>>;

/**
 * Maps our seven-member `Channel` onto the endpoint's four.
 *
 * A `Record<Channel, …>` rather than a `switch` with a default: if `Channel`
 * gains a member, this fails to compile instead of silently sending a wrong
 * channel. There is no fallback value on purpose — a mis-declared channel is the
 * kind of wrong that reporting absorbs without erroring.
 *
 * The three `MWEB*` variants and `MOBILE` all collapse to `MobileWeb`: the
 * endpoint distinguishes native-vs-browser and mobile-vs-desktop only, so our
 * finer iOS/Android split within mobile web has nowhere to go.
 *
 * **Brand-neutral by construction.** This maps to a *slot* in a brand set, not to
 * a value; the brand set itself is supplied by the caller from the running
 * label's product service (`IProductService.cornucopiaClient.cashierChannels`).
 * A new label therefore adds nothing here — it supplies its own set.
 */
const CASHIER_CHANNEL_SLOT_BY_CHANNEL: Record<Channel, keyof CashierChannelSet> = {
  IOS: 'IosNative',
  ANDROID: 'AndroidNative',
  MWEB: 'MobileWeb',
  MWEB_IOS: 'MobileWeb',
  MWEB_ANDROID: 'MobileWeb',
  MOBILE: 'MobileWeb',
  DESKTOP: 'DesktopWeb',
};

/**
 * The ReThink `ClientType` for the running device, picked from the running
 * label's brand set (`IProductService.cornucopiaClient.cashierChannels`). Sent
 * as the cashier-session `channel` and as the Cornucopia withdrawal tracking
 * `clientType`, so the two always agree — on Casino both read
 * `*_CASINO_RETHINK`, on Games `*_GAMES_RETHINK`.
 */
export function getCashierChannel(cashierChannels: CashierChannelSet): CashierChannel {
  return cashierChannels[CASHIER_CHANNEL_SLOT_BY_CHANNEL[App.instance.casino.getChannel()]];
}

/**
 * `ioBlackBox` is an Iovation device-fingerprint string. The orchestrator reads
 * it from `profile.iovationBlackbox`; this app ships no Iovation SDK and has no
 * equivalent source, so the caller currently has nothing real to pass.
 */
/**
 * Both session endpoints take an identical request — only the path differs — so
 * this is shared rather than duplicated, which keeps the credentials decision in
 * one place.
 *
 * `credentials: 'include'` / `mode: 'cors'` are the load-bearing part and match
 * the cookie-authenticated `/v1/tpapi/*` calls in `account/account.api.ts`.
 * Without them the cross-origin POST carries no `SESSION_ID` cookie and the
 * endpoint has no session to work from.
 *
 * No `authorization` header — PayFS auth is the `SESSION_ID` cookie XOR a Bearer, never both.
 */
function cashierSessionRequestDef(
  redirectUrl: string,
  ioBlackBox: string,
  cashierChannels: CashierChannelSet,
): RequestInit {
  const channel = getCashierChannel(cashierChannels);
  return {
    ...fetchSettings,
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      // PayFS reads the session channel from this header, not the body.
      'TPAPI-Client-Type': channel,
    },
    credentials: 'include',
    mode: 'cors',
    body: JSON.stringify({
      returnRedirectUrl: redirectUrl,
      channel,
      ioBlackBox,
    }),
  };
}

export function createDepositCashierSession(
  redirectUrl: string,
  ioBlackBox: string,
  cashierChannels: CashierChannelSet,
): ApiDef<DepositCashierSession> {
  return {
    path: DEPOSIT_CASHIER_SESSION_PATH,
    requestDef: cashierSessionRequestDef(redirectUrl, ioBlackBox, cashierChannels),
    mapper: (response) => response.json(),
  };
}

export function createWithdrawalCashierSession(
  redirectUrl: string,
  ioBlackBox: string,
  cashierChannels: CashierChannelSet,
): ApiDef<WithdrawalCashierSession> {
  return {
    path: WITHDRAWAL_CASHIER_SESSION_PATH,
    requestDef: cashierSessionRequestDef(redirectUrl, ioBlackBox, cashierChannels),
    mapper: (response) => response.json(),
  };
}

// -------------------------------
// Session allowance (payment-fs) — the pre-session eligibility gate
//
// Mirrors the ReThink `payments-orchestrator` fetcher
// (`packages/mods/payments-orchestrator/mod/src/services/session-allowance/fetchers.ts`
// in `rethink-monoverse @ 8f9dd0a138a`): same path, same method, no body. See
// `docs/integrations/cornucopia.md` → "Payment eligibility".
//
// This call is a second, narrower gate specific to Cornucopia's own backend,
// handled the same way as Sports and ReThink (Q6, decision 2026-10-01). `204`
// means proceed. Any failure is classified with `classifyAllowanceError`
// (`cornucopia-eligibility.ts`, a port of the orchestrator's
// `classifySessionAllowanceError.ts`) and handed to the hosted
// `<payments-eligibility>` MFE: a classified code (the nested `4xx`
// `sessionAllowanceError.errorCode`, or `UNAUTHORIZED` on `401`) as its
// `error-code`; anything unclassifiable (network failure, `5xx`, unknown body)
// with no code, so the MFE shows its own generic popup (its missing exit is
// PayTech's, Q40). No mapper is needed because `HttpService.call` already
// resolves a bare `204` to `undefined` (`http-service.ts`), and a non-2xx
// status throws — with the parsed body spread onto the error — before a mapper
// would run.
// -------------------------------

const DEPOSIT_SESSION_ALLOWANCE_PATH = '/v1/payfs/payint/payment/deposit-session-allowance';
const WITHDRAWAL_SESSION_ALLOWANCE_PATH = '/v1/payfs/payint/payment/withdrawal-session-allowance';

/**
 * Both allowance endpoints take an identical request — only the path differs —
 * so this is shared rather than duplicated, mirroring `cashierSessionRequestDef`
 * above. Same credentials rationale: the endpoint authenticates on the
 * `SESSION_ID` cookie scoped to the account host, so `credentials: 'include'` /
 * `mode: 'cors'` are load-bearing, not incidental.
 *
 * No `authorization` header — PayFS auth is the `SESSION_ID` cookie XOR a Bearer, never both.
 */
function sessionAllowanceRequestDef(): RequestInit {
  return {
    ...fetchSettings,
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    credentials: 'include',
    mode: 'cors',
  };
}

export function createDepositSessionAllowance(): ApiDef<void> {
  return {
    path: DEPOSIT_SESSION_ALLOWANCE_PATH,
    requestDef: sessionAllowanceRequestDef(),
  };
}

export function createWithdrawalSessionAllowance(): ApiDef<void> {
  return {
    path: WITHDRAWAL_SESSION_ALLOWANCE_PATH,
    requestDef: sessionAllowanceRequestDef(),
  };
}

const PAYMENT_DETAILS_PATH = '/v1/payfs/payint/payment/customers/payment-details';

/**
 * First-time-deposit details, for the Cornucopia payment funnel's
 * `firstDeposit*` GTM event names.
 *
 * Tracking-only — never gate a cashier on this. The orchestrator polls the same
 * endpoint alongside its session (`fetchFTDDetailsService`) and uses
 * `firstDepositTransactionId` to decide whether a *specific* successful
 * transaction was the first deposit. PAM's `has-deposit` cannot answer that: it
 * returns a bare boolean, so a `success` event could only be classified by
 * "had they deposited before", which is wrong in the window where the deposit
 * has already landed in PAM.
 *
 * Same host and credentials as the other `payint/payment/*` calls: the account
 * host, `credentials: 'include'` + `mode: 'cors'`, because the endpoint
 * authenticates on the `SESSION_ID` cookie scoped there. It is one of the six
 * route patterns with credentialed CORS configured in `payment-flow-service`.
 *
 * No `authorization` header — PayFS auth is the `SESSION_ID` cookie XOR a Bearer, never both.
 */
export function getCustomerPaymentDetails(): ApiDef<CustomerPaymentDetails> {
  return {
    path: PAYMENT_DETAILS_PATH,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: {
        'content-type': 'application/json',
      },
      credentials: 'include',
      mode: 'cors',
    },
    mapper: (response) => response.json().then((body) => body as CustomerPaymentDetails),
  };
}

const USER_MIGRATION_STATUS_PATH = '/v1/payfs/payint/payment/user-migration-status';
/**
 * PAYT-2184: which cashier the user belongs to during the eBET → Cornucopia
 * migration. Interpreted by `isCornucopiaUser()` (`cornucopia-migration-status.ts`);
 * never call it from anywhere else.
 *
 * Contract from ReThink (`rethink-monoverse @ f3d71a92481`,
 * `mods/core/mod/src/services/userMigrationStatus/fetchers.ts`): GET, no body,
 * `entity` query param identifying the label. The body shape is inconsistent
 * there (production hook reads a bare string, the fetcher spec mocks
 * `{ status }`), so the mapper returns the raw payload — parsed JSON when the
 * text is JSON, the trimmed text otherwise — and leaves interpretation to the gate.
 *
 * Account host, `credentials: 'include'` + `mode: 'cors'`: the endpoint
 * authenticates on the `SESSION_ID` cookie scoped there. No `authorization`
 * header — PayFS auth is either the `SESSION_ID` cookie or a Bearer jwt, never
 * both (confirmed by PayTech, 2026-10-05).
 *
 * Temporary — delete together with the migration gate.
 */
export function getUserMigrationStatus(entity: string): ApiDef<unknown> {
  return {
    path: `${USER_MIGRATION_STATUS_PATH}?${new URLSearchParams({ entity })}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
    },
    mapper: (response) =>
      response.text().then((text): unknown => {
        try {
          return JSON.parse(text);
        } catch {
          return text.trim();
        }
      }),
  };
}

export function getPreferences(jwt: string): ApiDef<UserPreferences> {
  return {
    path: '/v1/api/user/preferences',
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) =>
      response.json().then((bodyObject: { key: string; value: string }[]) => {
        const favoriteGames = bodyObject.find((pref) => pref.key === PrefKey.FAVOURITE_GAMES)?.value;
        const isBalanceHidden = bodyObject.find((pref) => pref.key === PrefKey.SHOW_BALANCE_DISABLED)?.value === 'true';
        const language = bodyObject.find((pref) => pref.key === PrefKey.LANGUAGE)?.value;
        const onboardingFlowsShown = bodyObject.find((pref) => pref.key === PrefKey.CASINO_ONBOARDING)?.value;
        const selectedTheme = bodyObject.find((pref) => pref.key === PrefKey.SELECTED_THEME)?.value;
        return {
          favoriteGames: new Set(favoriteGames ? favoriteGames.split(',') : []),
          isBalanceHidden,
          language: language ?? '',
          onboardingFlowsShown: new Set(onboardingFlowsShown ? onboardingFlowsShown.split(',') : []),
          selectedTheme,
        };
      }),
  };
}

export function storeUserPreferences(jwt: string, list: { key: string; value: string }[]): ApiDef<boolean> {
  return {
    path: '/v1/api/user/preferences',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
      body: JSON.stringify(list),
    },
    mapper: (response) => Promise.resolve(response.ok),
  };
}

export type TimestampTexts = { scheduledAt?: string; expiredAt?: string };

export type ActivationResponse = {
  activeUntil: string;
  lastActivatedAt: string;
  activitySummary: { scheduledAt?: string };
  realityCheck: TimestampTexts;
  longContinuousPlay: { coolDown: TimestampTexts };
  longNightPlay: { coolDown: TimestampTexts };
};

export function getActivation(jwt: string): ApiDef<ActivationResponse> {
  return {
    path: '/v1/api/user-activation/activated-user',
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => ({ ...body })),
  };
}

export function pollActivityChange(jwt: string): ApiDef<boolean> {
  return {
    path: '/v1/api/user-activation/activated-user/poll',
    requestDef: {
      ...fetchSettings,
      method: 'POST',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => Promise.resolve(response.status === 200),
  };
}

export function requestedActivitySummary(jwt: string): ApiDef<UserActivitySummaryAPI> {
  return {
    path: '/v1/api/user-activation/user/requested-activity-summary',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => {
      if (response.status === 204) {
        return Promise.resolve({ ...response, status: response.status });
      }
      return response.json().then((body) => ({ ...body }));
    },
  };
}

export function confirmPlayerStatistics(jwt: string): ApiDef<{ scheduledAt: string }> {
  return {
    path: '/v1/api/user-activation/user/confirmed-activity-summary',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => ({ ...body })),
  };
}

export function showRealityCheck(jwt: string): ApiDef<{ scheduledAt: string }> {
  return {
    path: '/v1/api/user-activation/user/requested-reality-check',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => ({ ...body })),
  };
}

export function confirmRealityCheck(jwt: string): ApiDef<{ coolDown: TimestampTexts }> {
  return {
    path: '/v1/api/user-activation/user/confirmed-reality-check',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => ({ ...body })),
  };
}

export function confirmLongPlayCheck(
  jwt: string,
  type: LongPlayConfirmType,
): ApiDef<{ coolDown: { expiredAt: string; scheduledAt: string } }> {
  return {
    path: `/v1/api/user-activation/markers/confirm?type=${type === 'long' ? 'long-continuous-play' : 'long-night-play'}`,
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => {
      if (response.status === 204) return Promise.resolve(undefined as any);
      return response.json().then((body) => ({ ...body }));
    },
  };
}

export type LongPlayConfirmType = 'long' | 'night';

export function panicModeInitiated(jwt: string): ApiDef<any> {
  return {
    path: '/v1/api/compliance/panic',
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => {
      if (response.status === 204) {
        return Promise.resolve({ ...response, status: response.status });
      }

      return response.json().then((body) => ({ ...body }));
    },
  };
}

export function sendUserConfirmation(jwt: string, type: UserConfirmType) {
  return {
    path: `/v1/api/user/confirm/${type}`,
    requestDef: {
      ...fetchSettings,
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      },
    },
  };
}

export function getPaymentsHistory(jwt: string, options: GetPaymentsPayload): ApiDef<PaymentHistoryItem[]> {
  return {
    path: `/v1/api/payment/payments${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => body.payments),
  };
}

/**
 * `has-deposit` — the only account-level source this app has for
 * first-time-deposit (FTD) status. Used by `LowBalanceFlyerController`.
 *
 * Cornucopia's own `deposit/params` payload carries no FTD signal at all
 * (verified against `DepositParams`/`WithdrawalParams` source), and ReThink's
 * own FTD tracking logic (`apps/cashier/app/src/store/deposit/selectors.ts`
 * `isFirstDeposit`, in the `rethink-monoverse` snapshot) reads customer
 * profile state, not the payment payload either — confirming this is
 * account-level data by design, not something the payment flow itself knows.
 */
export function getHasDeposit(jwt: string): ApiDef<boolean> {
  return {
    path: '/v1/api/payment/has-deposit',
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => body.hasDeposit),
  };
}

export function getLimitsHistory(jwt: string, options: GetLimitsHistoryPayload): ApiDef<LimitsHistory[]> {
  return {
    path: `/v1/api/limits/history${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => body),
  };
}

export function getTransactions(
  jwt: string,
  options?: {
    from?: string;
    to?: string;
    limit?: number;
    skip?: number;
  },
): ApiDef<WalletTransaction[]> {
  return {
    path: `/v1/api/wallet/transactions${createQuery(options)}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => ({ ...body })),
  };
}

export function getTransactionDetails(jwt: string, id: string): ApiDef<WalletTransaction> {
  return {
    path: `/v1/api/wallet/transactions/${id}`,
    requestDef: {
      ...fetchSettings,
      method: 'GET',
      headers: new Headers({
        'content-type': 'application/json',
        authorization: `Bearer ${jwt}`,
      }),
    },
    mapper: (response) => response.json().then((body) => ({ ...body })),
  };
}
